import { assertUnreachable, Mutable } from '@evorait/pace-core';
import { on } from '@ngrx/store';
import { FormsConstants } from '../../../types/forms-constants';
import { FunctionalElements } from '../../../types/functional-elements';
import { RADIO_OPTION_TYPE } from '../../../types/radio-option';
import { updateCheckpoint, updateCheckpoints } from '../../actions/checkpoint.actions';
import { FormState } from '../../state/form.state';
import {
    setFunctionalElementToNA,
    setFunctionalElementToNo,
    setFunctionalElementToYes,
    updateStateAfterContentChanges,
} from './form-reducer.utilities';

export function updateCheckpointReducer(
    state: Readonly<FormState>,
    props: {
        xmlId: string;
        selected: RADIO_OPTION_TYPE | undefined;
    },
): Readonly<FormState> {
    const checkpointOptions = state.participantCopies[state.selectedParticipantCopyId].options[props.xmlId];
    if (typeof checkpointOptions === 'undefined') {
        throw new Error(`FormReducer: updateCheckpointReducer: Could not find options for checkpoint '${props.xmlId}'`);
    }
    const previouslySelectedOption = checkpointOptions.find((o) => o.selected);
    const previousSelection =
        typeof previouslySelectedOption !== 'undefined' ? previouslySelectedOption.optionType : undefined;

    if (previousSelection === props.selected) {
        return state;
    }

    // Update the option by removing the selected attribute from the previous selection and adding it to the new selection
    const updatedOptions = {
        [props.xmlId]: checkpointOptions.map((option) => {
            const selected = option.optionType === props.selected;

            // if we are editing the master, then update selectedInMaster. Otherwise we are in a participant copy and do
            // not update this value anymore.
            const selectedInMaster =
                state.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID
                    ? selected
                    : option.selectedInMaster;
            return { ...option, selected, selectedInMaster, contentLoadedFromXml: false };
        }),
    };
    const matchingCheckpoint = Object.values(state.checkpoints).find((checkpoint) => checkpoint.xmlId === props.xmlId);
    if (typeof matchingCheckpoint === 'undefined') {
        throw new Error(
            `FormReducer: updateCheckpointReducer: Checkpoint with xmlId '${props.xmlId}' not existing while reducing action 'updateCheckpoint'`,
        );
    }

    // Check if there are dependent functional elements and update them as well
    const newFunctionalElement: Mutable<FunctionalElements> = {};
    const dependency = state.checkpointDependencies[props.xmlId];
    if (typeof dependency !== 'undefined') {
        const oldFunctionalElement =
            state.participantCopies[state.selectedParticipantCopyId].functionalElements[dependency.functionalElement];
        switch (props.selected) {
            case undefined:
                // If 'Yes' was selected, we don't need to change anything. Otherwise reset the functional element.
                newFunctionalElement[dependency.functionalElement] =
                    previousSelection === RADIO_OPTION_TYPE.YES
                        ? oldFunctionalElement
                        : setFunctionalElementToYes(oldFunctionalElement);
                break;
            case RADIO_OPTION_TYPE.YES:
                newFunctionalElement[dependency.functionalElement] = setFunctionalElementToYes(oldFunctionalElement);
                break;
            case RADIO_OPTION_TYPE.NO:
                newFunctionalElement[dependency.functionalElement] = setFunctionalElementToNo(oldFunctionalElement);
                break;
            case RADIO_OPTION_TYPE.NA:
                newFunctionalElement[dependency.functionalElement] = setFunctionalElementToNA(oldFunctionalElement);
                break;
            default:
                assertUnreachable(props.selected);
        }
    }

    return updateStateAfterContentChanges(
        state,
        {
            ...state.participantCopies[state.selectedParticipantCopyId].functionalElements,
            ...newFunctionalElement,
        },
        {
            ...state.participantCopies[state.selectedParticipantCopyId].options,
            ...updatedOptions,
        },
    );
}

// This could also be implemented as an effect that emits the required updateCheckpoint actions, but it would be much
// slower due to the multitude of actions.
export function updateCheckpointsReducer(
    state: Readonly<FormState>,
    props: {
        xmlIds: string[];
        selected: RADIO_OPTION_TYPE | undefined;
    },
): Readonly<FormState> {
    let updatedState = state;
    props.xmlIds.forEach((xmlId) => {
        updatedState = updateCheckpointReducer(updatedState, {
            selected: props.selected,
            xmlId,
        });
    });

    return updatedState;
}

export const checkpointReducer = [
    on<FormState, Array<typeof updateCheckpoint>>(updateCheckpoint, updateCheckpointReducer),
    on<FormState, Array<typeof updateCheckpoints>>(updateCheckpoints, updateCheckpointsReducer),
];
